import { linkIdentityEquals, type Task, type TaskRemoteLink } from "@app/core";
import type { StorageAdapter } from "../adapter/adapter.js";
import { ackedColumn, clampAck, type SyncDestination } from "./mut.js";

export interface SetRemoteLinkResult {
  /** The row's `link_rev` after the write. */
  linkRev: number;
  /** True when the IDENTITY moved (a new generation), false for an etag-only
   *  adopt. Only an identity change bumps `mut` (and so reaches the sync
   *  server); etag churn is device-local and dirties nothing. */
  identityChanged: boolean;
}

const TASK_CONTENT_SET =
  `title=?,notes=?,due_at=?,deadline_at=?,all_day=?,duration_minutes=?,priority=?,matrix_pos=?,matrix_hidden=?,custom_color=?,is_event=?,location=?,label_ids=?,reminder_offsets=?,tz=?,topic_ids=?,calendar_id=?,rev=?,status=?,completed_at=?,snoozed_until=?,recurrence_id=?,parent_id=?,subtask_parent_id=?,subtask_order=?,updated_at=?,deleted_at=?`;

/** Every content write goes through ONE of these three statements, and every one
 *  bumps `mut` — that is the repository chokepoint the sync engines' dirty state
 *  is derived from (dirty(dest) === mut > acked; see core sync/device-local.ts).
 *  The self-acknowledging variants land `acked = mut+1` in the SAME statement,
 *  against the same pre-update `mut` (SQLite SET semantics), so a write whose
 *  content came FROM a destination is atomically born clean for it — while an
 *  interleaved local edit bumps `mut` past the ack and stays dirty. */
const TASK_UPDATE_SQL = `UPDATE tasks SET ${TASK_CONTENT_SET},mut=mut+1 WHERE id=?`;
const TASK_UPDATE_SYNCED_SQL: Record<SyncDestination, string> = {
  server: `UPDATE tasks SET ${TASK_CONTENT_SET},mut=mut+1,server_acked_mut=mut+1 WHERE id=?`,
  google: `UPDATE tasks SET ${TASK_CONTENT_SET},mut=mut+1,google_acked_mut=mut+1 WHERE id=?`,
};

/** CRUD over the `tasks` table. Returns plain Task objects. */
export class TasksRepository {
  constructor(private readonly db: StorageAdapter) {}

  /** `synced` marks the row as already in step with that destination (an
   *  inbound sync/Google apply); a plain insert starts dirty for both. */
  async insert(t: Task, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      `INSERT INTO tasks (id,title,notes,due_at,deadline_at,all_day,duration_minutes,priority,matrix_pos,matrix_hidden,custom_color,is_event,location,label_ids,reminder_offsets,tz,topic_ids,calendar_id,external_id,ical_uid,etag,rev,source,external_calendar_id,link_rev,mut,server_acked_mut,google_acked_mut,status,completed_at,snoozed_until,recurrence_id,parent_id,subtask_parent_id,subtask_order,created_at,updated_at,deleted_at)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
      [
        t.id, t.title, t.notes, t.due_at, t.deadline_at, t.all_day ? 1 : 0, t.duration_minutes,
        t.priority, t.matrix_pos ? JSON.stringify(t.matrix_pos) : null, t.matrix_hidden ? 1 : 0,
        t.custom_color ?? null, t.is_event ? 1 : 0, t.location ?? "", JSON.stringify(t.label_ids ?? []),
        JSON.stringify(t.reminder_offsets ?? []), t.tz ?? null,
        JSON.stringify(t.topic_ids ?? []), t.calendar_id ?? null,
        t.external_id ?? null, t.ical_uid ?? null, t.etag ?? null,
        t.rev ?? 0, t.source ?? null, t.external_calendar_id ?? null,
        t.link_rev ?? 0,
        1, synced === "server" ? 1 : 0, synced === "google" ? 1 : 0,
        t.status, t.completed_at ?? null, t.snoozed_until ?? null,
        t.recurrence_id, t.parent_id, t.subtask_parent_id ?? null, t.subtask_order ?? 0,
        t.created_at, t.updated_at, t.deleted_at,
      ],
    );
  }

  /**
   * Update a task's CONTENT, bumping `mut`. The provider remote-link columns
   * (`external_id`, `ical_uid`, `etag`, `source`, `external_calendar_id`) are
   * deliberately NOT in this statement — {@link setRemoteLink} is their only
   * writer. Likewise the incoming object's `mut`/`*_acked_mut` values are
   * IGNORED: those columns belong to this chokepoint, never to callers.
   *
   * `synced` self-acknowledges the write for one destination — used by the sync
   * engines when the content they are writing came FROM that destination and is
   * therefore in step with it by construction. Every caller here does a
   * whole-row read-modify-write from a copy it read earlier (the zustand store,
   * a sync snapshot, a backup envelope); those copies go stale against the link
   * the Google reconciler adopts mid-flight, which is why the link columns are
   * excluded (see `TASK_REMOTE_LINK_FIELDS` in `@app/core`).
   */
  async update(t: Task, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      synced ? TASK_UPDATE_SYNCED_SQL[synced] : TASK_UPDATE_SQL,
      [
        t.title, t.notes, t.due_at, t.deadline_at, t.all_day ? 1 : 0, t.duration_minutes,
        t.priority, t.matrix_pos ? JSON.stringify(t.matrix_pos) : null, t.matrix_hidden ? 1 : 0,
        t.custom_color ?? null, t.is_event ? 1 : 0, t.location ?? "", JSON.stringify(t.label_ids ?? []),
        JSON.stringify(t.reminder_offsets ?? []), t.tz ?? null,
        JSON.stringify(t.topic_ids ?? []), t.calendar_id ?? null,
        t.rev ?? 0,
        t.status, t.completed_at ?? null, t.snoozed_until ?? null,
        t.recurrence_id, t.parent_id, t.subtask_parent_id ?? null, t.subtask_order ?? 0,
        t.updated_at, t.deleted_at, t.id,
      ],
    );
  }

  /** Adopt a server-assigned `rev` WITHOUT touching content, `mut`, or any ack —
   *  used when a mid-sync local edit is being protected: the kept local content
   *  must stay dirty, but it has to carry the server's rev so its own push wins
   *  the rev-first comparison instead of being discarded as stale. */
  async adoptRev(id: string, rev: number): Promise<void> {
    await this.db.execute(`UPDATE tasks SET rev=? WHERE id=?`, [rev, id]);
  }

  /**
   * Write ONLY the provider remote-link columns — the single sanctioned path for
   * them, used by the Google reconciler when it adopts an event's identity or
   * unlinks a task from one. Touches no content column and never `updated_at`
   * (adopting an etag is not a user edit and must not mark the row dirty).
   *
   * Bumps `link_rev` AND `mut` whenever the IDENTITY changes, including to null.
   * `link_rev` orders add / replace / unlink across devices (since `updated_at`
   * deliberately does not move here); the `mut` bump is what makes the change
   * visible to the sync server's per-row dirty fact — a link adoption must reach
   * the server or a peer keeps an unlinked copy and creates a duplicate event.
   * Living in the same durable row as the link itself, the fact survives a crash
   * between this write and any later acknowledgement. An etag-only write bumps
   * neither, so the constant etag churn never dirties anything.
   */
  async setRemoteLink(id: string, link: TaskRemoteLink): Promise<SetRemoteLinkResult> {
    const current = await this.findById(id);
    if (!current) return { linkRev: 0, identityChanged: false };
    const identityChanged = !linkIdentityEquals(current, link);
    const nextRev = identityChanged ? (current.link_rev ?? 0) + 1 : current.link_rev ?? 0;
    await this.db.execute(
      identityChanged
        ? `UPDATE tasks SET external_id=?,ical_uid=?,etag=?,source=?,external_calendar_id=?,link_rev=?,mut=mut+1 WHERE id=?`
        : `UPDATE tasks SET external_id=?,ical_uid=?,etag=?,source=?,external_calendar_id=?,link_rev=? WHERE id=?`,
      [
        link.external_id ?? null, link.ical_uid ?? null, link.etag ?? null,
        link.source ?? null, link.external_calendar_id ?? null, nextRev, id,
      ],
    );
    return { linkRev: nextRev, identityChanged };
  }

  /**
   * Write an ALREADY-RESOLVED link block verbatim, including its `link_rev` — the
   * sync apply path, where the revision was decided by `resolveRemoteLink` and
   * must not be bumped again (that would mint a new generation out of adopting
   * someone else's). Bumps NO counter: a peer-taught link came from the server
   * and points at Google, so neither destination is owed anything for it.
   * Never use this from the reconciler; use `setRemoteLink`.
   */
  async applyRemoteLink(id: string, link: TaskRemoteLink, linkRev: number): Promise<void> {
    await this.db.execute(
      `UPDATE tasks SET external_id=?,ical_uid=?,etag=?,source=?,external_calendar_id=?,link_rev=? WHERE id=?`,
      [
        link.external_id ?? null, link.ical_uid ?? null, link.etag ?? null,
        link.source ?? null, link.external_calendar_id ?? null, linkRev, id,
      ],
    );
  }

  /**
   * Acknowledge that a sync destination now holds this row's content as of
   * `upTo` (the `mut` the upload's body was READ at — never a value observed
   * after later writes). Clamped: never past the row's current `mut`, never
   * backwards — so an edit that landed mid-flight keeps the row dirty and simply
   * pushes on the next pass.
   */
  async ackMut(id: string, dest: SyncDestination, upTo: number): Promise<void> {
    const current = await this.findById(id);
    if (!current) return;
    const next = clampAck(current, dest, upTo);
    if (next === null) return;
    await this.db.execute(`UPDATE tasks SET ${ackedColumn(dest)}=? WHERE id=?`, [next, id]);
  }

  async listActive(): Promise<Task[]> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM tasks WHERE deleted_at IS NULL`,
    );
    return rows.map(fromRow);
  }

  /** Every row INCLUDING soft-deleted tombstones — for sync, which must propagate
   *  deletes. Don't use for UI (which wants only active rows). */
  async listAll(): Promise<Task[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM tasks`);
    return rows.map(fromRow);
  }

  async findById(id: string): Promise<Task | null> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM tasks WHERE id=?`, [id],
    );
    return rows[0] ? fromRow(rows[0]) : null;
  }

  async hardDelete(id: string): Promise<void> {
    await this.db.execute(`DELETE FROM tasks WHERE id=?`, [id]);
  }
}

/** matrix_pos is a JSON `{urgency, importance, urgencyAuto?}` string; tolerate
 *  null/garbage → null. `urgencyAuto` (optional) marks the urgency axis as
 *  deadline-derived; absent = manual. */
function parseMatrixPos(v: unknown): Task["matrix_pos"] {
  if (typeof v !== "string" || v === "") return null;
  try {
    const parsed = JSON.parse(v) as { urgency?: unknown; importance?: unknown; urgencyAuto?: unknown } | null;
    if (!parsed || typeof parsed !== "object") return null;
    const urgency = Number(parsed.urgency);
    const importance = Number(parsed.importance);
    if (!Number.isFinite(urgency) || !Number.isFinite(importance)) return null;
    return parsed.urgencyAuto ? { urgency, importance, urgencyAuto: true } : { urgency, importance };
  } catch {
    return null;
  }
}

/** label_ids is persisted as a JSON array string; tolerate null/garbage → []. */
function parseLabelIds(v: unknown): string[] {
  if (typeof v !== "string" || v === "") return [];
  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/** reminder_offsets is a JSON array of minutes; tolerate null/garbage → []. */
function parseOffsets(v: unknown): number[] {
  if (typeof v !== "string" || v === "") return [];
  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.map(Number).filter((n) => Number.isFinite(n)) : [];
  } catch {
    return [];
  }
}

function fromRow(r: Record<string, unknown>): Task {
  return {
    id: String(r["id"]),
    title: String(r["title"] ?? ""),
    notes: String(r["notes"] ?? ""),
    due_at: (r["due_at"] as string | null) ?? null,
    deadline_at: (r["deadline_at"] as string | null) ?? null,
    all_day: Number(r["all_day"] ?? 0) === 1,
    duration_minutes: Number(r["duration_minutes"] ?? 0),
    priority: (r["priority"] as Task["priority"]) ?? "none",
    matrix_pos: parseMatrixPos(r["matrix_pos"]),
    matrix_hidden: Number(r["matrix_hidden"] ?? 0) === 1,
    custom_color: (r["custom_color"] as string | null) ?? null,
    is_event: Number(r["is_event"] ?? 0) === 1,
    location: String(r["location"] ?? ""),
    label_ids: parseLabelIds(r["label_ids"]),
    reminder_offsets: parseOffsets(r["reminder_offsets"]),
    tz: (r["tz"] as string | null) ?? null,
    topic_ids: parseLabelIds(r["topic_ids"]),
    calendar_id: (r["calendar_id"] as string | null) ?? null,
    external_id: (r["external_id"] as string | null) ?? null,
    ical_uid: (r["ical_uid"] as string | null) ?? null,
    etag: (r["etag"] as string | null) ?? null,
    rev: Number(r["rev"] ?? 0),
    source: (r["source"] as string | null) ?? null,
    external_calendar_id: (r["external_calendar_id"] as string | null) ?? null,
    link_rev: Number(r["link_rev"] ?? 0),
    mut: Number(r["mut"] ?? 0),
    server_acked_mut: Number(r["server_acked_mut"] ?? 0),
    google_acked_mut: Number(r["google_acked_mut"] ?? 0),
    status: (r["status"] as Task["status"]) ?? "open",
    completed_at: (r["completed_at"] as string | null) ?? null,
    snoozed_until: (r["snoozed_until"] as string | null) ?? null,
    recurrence_id: (r["recurrence_id"] as string | null) ?? null,
    parent_id: (r["parent_id"] as string | null) ?? null,
    subtask_parent_id: (r["subtask_parent_id"] as string | null) ?? null,
    subtask_order: Number(r["subtask_order"] ?? 0),
    created_at: String(r["created_at"]),
    updated_at: String(r["updated_at"]),
    deleted_at: (r["deleted_at"] as string | null) ?? null,
  };
}
